import { createSlice } from '@reduxjs/toolkit';
import { userApi } from '@/store/api/userApi';
import { UserProfile, WalletInfo } from '@/types/user';
import { notificationApi } from '@/store/api/contactSupportApi';
import { agentContactSupportApi } from '@/store/api/agentContactSupportApi';

export interface ContactSupportDetails {
  whatsapp: string | null;
  telegram: string | null;
  imo?: string | null;
  firstname?: string | null;
  lastname?: string | null;
  profileImage?: string | null;
}

interface AccountState {
  profile: UserProfile | null;
  wallet: WalletInfo;
  contactSupport: {
    owner: ContactSupportDetails;
    agent: ContactSupportDetails;
  };
}

const initialState: AccountState = {
  profile: null,
  wallet: {
    mainBalance: 0,
    bonusBalance: 0,
    exposure: 0,
    lockedAmount: 0,
  },
  contactSupport: {
    owner: { whatsapp: null, telegram: null },
    agent: { whatsapp: null, telegram: null, imo: null },
  },
};

const emptyContactSupport = (): ContactSupportDetails => ({
  whatsapp: null,
  telegram: null,
  imo: null,
});

const normalizeContactSupport = (
  contactSupport: ContactSupportDetails | null | undefined
): ContactSupportDetails => ({
  ...emptyContactSupport(),
  ...contactSupport,
});

const isSameWallet = (current: WalletInfo, next: WalletInfo) => {
  return (
    current.mainBalance === next.mainBalance &&
    current.bonusBalance === next.bonusBalance &&
    current.exposure === next.exposure &&
    current.lockedAmount === next.lockedAmount
  );
};

export const accountSlice = createSlice({
  name: 'account',
  initialState,
  reducers: {
    resetAccount: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      // Profile
      .addMatcher(userApi.endpoints.getUserProfile.matchFulfilled, (state, { payload }) => {
        state.profile = payload;
      })

      // Wallet
      .addMatcher(userApi.endpoints.getUserWallets.matchFulfilled, (state, { payload }) => {
        if (isSameWallet(state.wallet, payload)) {
          return;
        }

        state.wallet = payload;
      })

      .addMatcher(notificationApi.endpoints.getContactSupport.matchFulfilled, (state, { payload }) => {
        state.contactSupport.owner = normalizeContactSupport(payload);
      })

      .addMatcher(
        agentContactSupportApi.endpoints.getAgentContactSupport.matchFulfilled,
        (state, { payload }) => {
          state.contactSupport.agent = normalizeContactSupport(payload.contacts?.upline);
        }
      );
  },
});

export const { resetAccount } = accountSlice.actions;
